import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Inbox, ChevronLeft, Mail, Star } from 'lucide-react'
import { PageShell } from '../../../components/layout/PageShell'
import { SecondaryRail, RailItem, RailSection } from '../../../components/layout/SecondaryRail'
import { PageBrow } from '../../../components/layout/PageBrow'
import { ListDetail } from '../../../components/layout/ListDetail'
import { EmptyState } from '../../../components/patterns'
import { useAuthStore } from '../../../stores/authStore'
import { demoInbox, type DemoMail } from './mockData'

export default function ThreeRailDemo() {
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (!user?.isAdmin) return <Navigate to="/" replace />

  const selected = demoInbox.find((m) => m.id === selectedId) ?? null
  const unread = demoInbox.filter((m) => m.unread).length

  const rail2 = (
    <SecondaryRail icon={<Inbox size={15} />} title="Mail" search={{ value: '', onChange: () => {}, placeholder: 'Search mail...' }}>
      <RailItem active icon={<Inbox size={14} />} label="Inbox" count={unread} />
      <RailSection label="Folders">
        <RailItem icon={<Star size={14} />} label="Starred" />
        <RailItem icon={<Mail size={14} />} label="Sent" />
      </RailSection>
    </SecondaryRail>
  )

  const brow = (
    <PageBrow
      onBack={() => navigate('/admin')}
      left={<div className="flex items-center gap-2.5"><Inbox size={18} className="text-primary" /><h1 className="text-[15px] font-semibold text-foreground">Inbox</h1><span className="h-1 w-1 rounded-full bg-muted-foreground/50" /><span className="text-[13px] text-muted-foreground">3-rail layout demo - mock data</span></div>}
    />
  )

  const list = (
    <ul>
      {demoInbox.map((m) => (
        <li key={m.id}>
          <button
            type="button"
            onClick={() => setSelectedId(m.id)}
            className={`flex w-full flex-col gap-0.5 border-b border-border/60 px-4 py-3 text-left transition hover:bg-accent ${selectedId === m.id ? 'bg-accent' : ''}`}
          >
            <div className="flex items-center gap-2">
              {m.unread && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
              <span className={`flex-1 truncate text-sm ${m.unread ? 'font-semibold text-foreground' : 'text-foreground'}`}>{m.from}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{m.time}</span>
            </div>
            <span className="truncate text-sm text-foreground">{m.subject}</span>
            <span className="truncate text-xs text-muted-foreground">{m.preview}</span>
          </button>
        </li>
      ))}
    </ul>
  )

  const detail = selected ? <MailDetail mail={selected} onBack={() => setSelectedId(null)} /> : undefined

  return (
    <PageShell brow={brow} rail2={rail2} scroll={false}>
      <ListDetail
        list={list}
        detail={detail}
        showDetailOnMobile={!!selected}
        emptyDetail={<div className="flex h-full items-center justify-center p-8"><EmptyState size="sm" icon={<Mail size={28} />} title="No message selected" description="Pick a message from the list to read it." /></div>}
      />
    </PageShell>
  )
}

function MailDetail({ mail, onBack }: { mail: DemoMail; onBack: () => void }) {
  return (
    <div className="px-6 py-6">
      <button type="button" onClick={onBack} className="-ml-1 mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground lg:hidden">
        <ChevronLeft size={16} /> Inbox
      </button>
      <h1 className="text-xl font-semibold text-foreground">{mail.subject}</h1>
      <div className="mt-1 text-sm text-muted-foreground">{mail.from} - {mail.time}</div>
      <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground">{mail.body}</p>
    </div>
  )
}
